import React, { useState } from 'react'
import isFunction from 'lodash/isFunction'
import { Link } from 'react-router-dom'

import { Table, TableHeader, TableHeaderCell, TableRow, SortHeader, TableBody, TableBodyCell } from '../../../lib/table'
import { sortByString } from '../../../lib/sort-by-string'
import { Company } from '../../redux/api/web-api'
import { isSupportDomain } from '../../../lib/currentDomain'
import DropdownMenu, { DropdownMenuItem } from '../../../common/components/DropdownMenu'
import { GoToIcon } from '../../../lib/icons/goto-icon'
import TrashIcon from '../../../lib/icons/trash-icon/trash-icon'
import ConfirmationModal from '../../../ui/ConfirmationModal'
import useText from '../../hooks/useText'

export type OrganisationsTableProps = {
    data: Company[]
    loading: boolean
    openCompanyAdmin?: (id?: string) => void
    removeOrganisation?: (id?: string) => void
    filter?: string
}

export default React.memo(function OrganisationsTable({
    data,
    loading,
    openCompanyAdmin,
    removeOrganisation,
    filter,
}: OrganisationsTableProps) {
    const text = useText()
    const [deleteOrganisationModalIsOpened, setDeleteOrganisationModalIsOpened] = useState(false)
    const [organisationId, setOrganisationId] = useState<number | null>(null)
    const [sorting, setSorting] = useState<{
        sortType: 'name' | 'description' | 'email' | 'phone' | 'partnerName'
        descending: boolean
    }>({
        sortType: 'name',
        descending: false,
    })

    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell className="heading5" data-testid="cell-name">
                            <SortHeader
                                code="lblName"
                                id="name"
                                sortType={sorting.sortType}
                                descending={sorting.descending}
                                onClick={(sortType, descending) => setSorting({ sortType, descending })}
                            />
                        </TableHeaderCell>
                        {!isSupportDomain && (
                            <TableHeaderCell className="heading5" data-testid="cell-desc">
                                <SortHeader
                                    code="lblDescription"
                                    id="description"
                                    sortType={sorting.sortType}
                                    descending={sorting.descending}
                                    onClick={(sortType, descending) => setSorting({ sortType, descending })}
                                />
                            </TableHeaderCell>
                        )}
                        {isSupportDomain && (
                            <TableHeaderCell className="heading5" data-testid="cell-partner">
                                <SortHeader
                                    code="lblPartnerField"
                                    id="partnerName"
                                    sortType={sorting.sortType}
                                    descending={sorting.descending}
                                    onClick={(sortType, descending) => setSorting({ sortType, descending })}
                                />
                            </TableHeaderCell>
                        )}
                        <TableHeaderCell className="heading5" data-testid="cell-email">
                            <SortHeader
                                code="lblEmail"
                                id="email"
                                sortType={sorting.sortType}
                                descending={sorting.descending}
                                onClick={(sortType, descending) => setSorting({ sortType, descending })}
                            />
                        </TableHeaderCell>
                        <TableHeaderCell className="heading5" data-testid="cell-phone">
                            <SortHeader
                                code="lblPhone"
                                id="phone"
                                sortType={sorting.sortType}
                                descending={sorting.descending}
                                onClick={(sortType, descending) => setSorting({ sortType, descending })}
                            />
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody isLoading={loading}>
                    {data
                        ?.filter((org) => {
                            if (!filter) return true
                            const lowerCaseFilter = filter.toLowerCase()

                            return (
                                org.name?.toLowerCase()?.includes(lowerCaseFilter) ||
                                org.description?.toLowerCase()?.includes(lowerCaseFilter) ||
                                org.email?.toLowerCase()?.includes(lowerCaseFilter)
                            )
                        })
                        .sort((a, b) =>
                            sortByString(
                                (a[sorting.sortType] as string) ?? '',
                                (b[sorting.sortType] as string) ?? '',
                                sorting.descending
                            )
                        )
                        .map((org) => (
                            <TableRow key={org.id} data-testid={`row-${org.id}`}>
                                <TableBodyCell data-testid={`cell-name-${org.id}`}>
                                    <Link
                                        to={`/company/${org.id}`}
                                        className="btn btn-link btn-xs"
                                        data-testid="btn-name"
                                    >
                                        {org.name}
                                    </Link>
                                </TableBodyCell>
                                {!isSupportDomain && (
                                    <TableBodyCell data-testid={`cell-desc-${org.id}`}>{org.description}</TableBodyCell>
                                )}
                                {isSupportDomain && (
                                    <TableBodyCell data-testid={`cell-partner-${org.id}`}>
                                        {org.partnerName}
                                    </TableBodyCell>
                                )}
                                <TableBodyCell data-testid={`cell-email-${org.id}`}>{org.email}</TableBodyCell>
                                <TableBodyCell>{org.phone}</TableBodyCell>
                                {!isSupportDomain && (
                                    <TableBodyCell data-testid={`cell-goto-${org.id}`}>
                                        <DropdownMenu data-testid={`dropdownMenu-${org.id}`}>
                                            <DropdownMenuItem
                                                onClick={() => isFunction(openCompanyAdmin) && openCompanyAdmin(org.id)}
                                                text={text('btnGoToOrganisation')}
                                                icon={<GoToIcon size={14} />}
                                                key="btnGoToOrg"
                                                data-testid={`goTo-${org.id}`}
                                            ></DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => {
                                                    setDeleteOrganisationModalIsOpened(true)
                                                    setOrganisationId(org?.id)
                                                }}
                                                text={text('btnDeleteOrganisation')}
                                                data-testid={`remove-${org.id}`}
                                                icon={<TrashIcon size={14} />}
                                            ></DropdownMenuItem>
                                        </DropdownMenu>
                                    </TableBodyCell>
                                )}
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            {deleteOrganisationModalIsOpened && (
                <ConfirmationModal
                    title={text('btnDeleteOrganisation')}
                    message={text('msgDeleteCompanyConfirmation')}
                    onConfirm={() => {
                        isFunction(removeOrganisation) && organisationId && removeOrganisation(organisationId)
                        setDeleteOrganisationModalIsOpened(false)
                    }}
                    onClose={() => {
                        setDeleteOrganisationModalIsOpened(false)
                        setOrganisationId(null)
                    }}
                />
            )}
            {loading && <div className="shimmer-container shimmer-small" data-testid="loader"></div>}
        </>
    )
})
